import AccountContext from "features/EnterprisePortal/AccountContext";
import { DayActivityViewModel, DayVenueActivityViewModel, PortalClient } from "Services/API/API"
import { useApiClient } from "Services/API/useApiClient"
import React, { useContext, useEffect, useState } from "react"
import * as Icons from "Components/Icons";
import { Module, ModuleCaption } from "Components/Module";
import { useSelector } from "react-redux";
import classNames from "classnames";
import { Button, ButtonGroup } from "Components/ReactstrapSlim";
import moment from "moment";
import LsLoading from "Components/LsLoading";
import { ActivityRow, ActivityType } from "../ActivityRow";
import { brandingSelector } from "Utils/selectors";
import { VenueCalendarModal } from "Components/VenueCalendarModal";
import useMediaBreakpointDown from "Hooks/useMediaBreakpointDown";
import { CalendarDay } from "../CalendarDay";
import localMomentDate from "Utils/moment/localMomentDate";
import { ReservationDetailsModal } from "../Reservations/DetailsModal";

interface ThisWeekModuleProps {

}

export const ThisWeekModule: React.FC<ThisWeekModuleProps> = () => {
   const account = useContext(AccountContext);
   const portalClient = useApiClient(PortalClient);
   const [isModuleLoading, setIsModuleLoading] = useState(false);
   const [isLoading, setIsLoading] = useState(false);
   const [activities, setActivities] = useState<DayActivityViewModel[]>([]);
   const [selectedDayMemberActivities, setSelectedDayMemberActivities] = useState<DayVenueActivityViewModel[]>([]);
   const [selectedDayVenueActivities, setSelectedDayVenueActivities] = useState<DayVenueActivityViewModel[]>([]);
   const [selectedReservation, setSelectedReservation] = useState<{ id: Guid, occurrence?: string } | undefined>(undefined);
   const [selectedVenue, setSelectedVenue] = useState<DayVenueActivityViewModel | undefined>(undefined);
   const branding = useSelector(brandingSelector);
   const [activeDate, setActiveDate] = useState<moment.Moment>(moment().startOf("day"));
   const isMobile = useMediaBreakpointDown("xs");
   const [startDate, setStartDate] = useState<moment.Moment>(moment().startOf("isoWeek"));
   const [endDate, setEndDate] = useState<moment.Moment>(moment().endOf("isoWeek").add(-2, "days"));

   const loadCurrentWeekActivities = async (date: moment.Moment) => {
      try {
         setIsModuleLoading(true);
         await loadWeekDaysActivities(date);
      } catch (ex) {
         console.log(ex);
      } finally {
         setIsModuleLoading(false);
      }
   }

   const switchToOtherWeekActivities = async (date: moment.Moment) => { 
      try {
         setIsLoading(true);
         await loadWeekDaysActivities(date);
      } catch (ex) {
         console.log(ex);
      } finally {
         setIsLoading(false);
      }
   }

   const loadWeekDaysActivities = async (date: moment.Moment) => {
      const data = await portalClient.getActivities(account.id, date.toISOString());
      setActivities(data);
      const selectedDayActivity = data.find(day => moment(day.date).isSame(date)) ?? data[0];
      setSelectedDay(selectedDayActivity);
      setStartDate(moment(data[0].date));
      setEndDate(moment(data[data.length - 1].date));
   }

   const setSelectedDay = (dayActivity: DayActivityViewModel) => {
      setSelectedDayMemberActivities(dayActivity.memberActivities);
      setSelectedDayVenueActivities(dayActivity.venueActivities);
      setActiveDate(moment(dayActivity.date));
   }

   const onReservationClick = (activity: DayVenueActivityViewModel) => {
      setSelectedReservation({ id: activity.reservationId, occurrence: activity.occurrenceDate });
   }

   const onExploreLocationClick = (e: React.MouseEvent) => {
      e.preventDefault();
        const selectedElement = document.getElementById("explore-locations-id");
        if (selectedElement && document.scrollingElement) {
            document.scrollingElement.scrollTop += selectedElement.getBoundingClientRect().top - 64;
        }
   }

   useEffect(() => {
      loadCurrentWeekActivities(activeDate);
   }, []);

   return <Module size="lg" loading={isModuleLoading} style={{ minHeight: isMobile ? "194px" : "284px" }} className={classNames({"no-activity-state": !isLoading && selectedDayMemberActivities.length == 0 && selectedDayVenueActivities.length == 0})} id="social-activity-id">
      <ModuleCaption className="calendar-caption">{moment(startDate).add(-2, "days").isSameOrBefore(moment(), "day") && endDate.isSameOrAfter(moment(), "day") ? "This Week" : `Week of ${startDate.format("MMMM D")} - ${endDate.format("MMMM D")}`}
         <div>
            { isMobile && <div className="current-month">{activeDate.format("MMMM YYYY")}</div> }
            <ButtonGroup className={classNames("module-pager-wrapper")}>
               <Button className="btn-arrow" color="arctic-20" size="sm" onClick={() => switchToOtherWeekActivities(moment(activeDate).add(-7, "days"))} disabled={isLoading}><Icons.ArrowLeft /></Button>
               { !isMobile && <Button color="arctic-20" size="sm" onClick={() => switchToOtherWeekActivities(moment().startOf("day"))} disabled={isLoading}>Today</Button> }
               <Button className="btn-arrow" color="arctic-20" size="sm" onClick={() => switchToOtherWeekActivities(moment(activeDate).add(7, "days"))} disabled={isLoading}><Icons.ArrowRight /></Button>
            </ButtonGroup>
         </div>
      </ModuleCaption>
      
      <div className={classNames("calendar-wrapper", { "reloading": isLoading })}>
         <LsLoading isLoading={isLoading}/>
         {
            activities.map((activity, index) => <CalendarDay key={index} activity={activity} date={activeDate} onClick={setSelectedDay} index={index} />)
         }
      </div>
      {
         !isLoading && selectedDayMemberActivities.length == 0 && selectedDayVenueActivities.length == 0 && <div className="no-activity-wrapper">
            <div className="no-activity-title">No reservations for {activeDate.format("MMMM Do")}</div>
               <div className="no-activity-subtitle">Explore great spaces to work nearby!</div>
               <div className="pt-px24">
                  <Button color="primary" onClick={onExploreLocationClick}>Explore locations nearby</Button>
               </div>
         </div>
      }
      {
         !isLoading && <div className="my-reservation-section">
         {
            selectedDayMemberActivities.length > 0 && <>
               <div className="my-reservation-header-wrapper">
                  <div className="my-reservation-caption">My reservations</div>
                  <div><a href={`${branding?.url}/reservations`} target="__blank">View all</a></div>
               </div>
               <div className="my-reservation-body">
               {
                  selectedDayMemberActivities.map((activity, index) => <ActivityRow key={index} activity={activity} activeDate={activeDate} activityType={ActivityType.My} onActivityClick={onReservationClick} />)
               }
               </div>
            </>
         }
         {
            selectedDayMemberActivities.length == 0 && selectedDayVenueActivities.length > 0 && <div className="no-reservations-state card">
               <div className="no-reservation-image"><Icons.Calendar /></div>
               <div className="no-reservation-body">
                  <div className="no-reservation-data">
                     <div className="no-reservation-title">No reservations for {activeDate.format("MMMM Do")}</div>
                     <div className="no-reservation-subtitle">Join your coworkers who are working nearby!</div>
                  </div>
                  <div className="no-reservation-link">
                     <a href="#" onClick={onExploreLocationClick}>Explore locations nearby</a>
                  </div>
               </div>
            </div>
         }
         </div>
      }
      {
         !isLoading && selectedDayVenueActivities.length > 0 && <div className="my-coworker-section">
            <div className="my-coworker-header">My coworkers</div>
            <div className="my-coworker-body">
            {
               selectedDayVenueActivities.map((activity, index) => <ActivityRow key={index} activity={activity} activeDate={activeDate} activityType={ActivityType.Coworker} onActivityClick={() => setSelectedVenue(activity)} />)
            }
            </div>
         </div>
      }
      { selectedReservation && <ReservationDetailsModal isOpen={true} toggle={() => setSelectedReservation(undefined)} reservation={selectedReservation.id} occurrenceDate={selectedReservation.occurrence} onUpdateReservation={() => loadWeekDaysActivities(activeDate)} /> }
      { selectedVenue?.venueId && <VenueCalendarModal isOpen={true} venueId={selectedVenue.venueId} selectedDate={localMomentDate(activeDate).toISOString()} toggle={() => setSelectedVenue(undefined)} refreshUpcomingReservationList={() => loadWeekDaysActivities(activeDate)} /> }
   </Module>
}